/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * @file Limits Provider for signedIn Acrobat.
 */

import EventEmitter from 'eventemitter3';
import { getSingletonFunction } from '../core/ProviderUtil';
// eslint-disable-next-line import/no-cycle
import { providers } from './Providers';
import DcapiAPI from './DcapiAPI';
import { auth2 } from './Auth2API';
import globalContextManager from '../core/GlobalContextManager';
import { setItem } from '../core/CookieUtil';
import { getEnvVar, getHostEnv } from '../core/EnvUtil';
import type {
  Limit, LimitsInfo, IVerbLimitsProvider,
} from '../types/Limits';
import type { IProviderStatic } from '../types/Provider';
import MeterIQLimitsProvider, { UBER_LIMIT_EXCEEDED_ERROR_CODE } from './MeterIQLimitsProvider';
import { logging } from './LoggingAPI';

const LIMITS_EXPIRY = 'limits2-expiry';

/**
 * Maps MeterIQ verb names to the short cookie names used by LimitsProvider.
 * Only verbs that have a corresponding anon cookie are included.
 * Cookie names must stay in sync with LimitsProvider.limitCookieMap.
 */
const VERB_COOKIE_MAP: Record<string, string> = {
  createpdf: 'cr_p_c',
  'export-pdf': 'ex_p_c',
  'combine-pdf': 'cb_p_c',
  'ocr-pdf': 'ocr_p_c',
  'compress-pdf': 'cm_p_ops',
  'organize-pdf': 'or_p_c',
};

const APP_ID_COOKIE_MAP: Record<string, string> = {
  adobe_com: 'ac',
  chrome_extension_viewer: 'cev',
  chrome_extension: 'ce',
  test_app: 'ta',
  integration: 'i',
};

const APP_ENV_COOKIE_MAP: Record<string, string> = {
  dev: 'd',
  stage: 's',
  prod: 'p',
};

/**
 * Maps DCAPI operation names to MeterIQ verb names for anonymous flows
 * that do not carry an x-user-action-name header.
 */
const ANON_OPERATION_VERB_MAP: Record<string, string> = {
  'assets.createpdf': 'createpdf',
  'assets.exportpdf': 'export-pdf',
  'assets.splitpdf': 'split-pdf',
};

/**
 * Maps pdf_actions[0] object keys to MeterIQ verb names, used when
 * the operation is assets.pdf_actions and no x-user-action-name is present.
 */
const ANON_PDF_ACTION_VERB_MAP: Record<string, string> = {
  optimize: 'compress-pdf',
  organize: 'organize-pdf',
  combine: 'combine-pdf',
  ocr: 'ocr-pdf',
  password_encrypt: 'protect-pdf',
};

/**
 * Write exhausted-limit cookies for anonymous MeterIQ users so that
 * other surfaces (acom, chrome extension) can suppress conversion entry
 * points without calling the API. Mirrors LimitsProvider.setExhaustedLimitsCookies.
 */
function setExhaustedVerbCookies(exhaustedVerbs: string[], expiresAt: number | null) {
  if (!exhaustedVerbs.length) return;

  const appEnv = APP_ENV_COOKIE_MAP[getHostEnv('server_env')];
  const appId = APP_ID_COOKIE_MAP[getEnvVar('app_identifier')];
  if (!appEnv || !appId) return;

  // Compute hours until expiry from MeterIQ expires_at (Unix seconds), fallback to 24 h
  let hours = 24;
  if (expiresAt && expiresAt > 0) {
    hours = Math.max((expiresAt - Date.now() / 1000) / 3600, 0);
  }
  if (!hours) return;

  const expires = new Date(Date.now() + hours * 1000 * 60 * 60).toUTCString();
  const domain = window.location.origin.endsWith('adobe.com') ? '.adobe.com' : undefined;

  exhaustedVerbs.forEach(verb => {
    const cookieName = VERB_COOKIE_MAP[verb];
    if (cookieName) {
      const cookieOptions = {
        domain, path: '/', expires, samesite: 'Strict', secure: true,
      };
      // Two cookies: one without app id (shared) and one with app id (app-specific)
      // Mirrors LimitsProvider behaviour — see TODO in LimitsProvider for retirement plan
      setItem(`${appEnv}_${cookieName}`, '1', cookieOptions);
      setItem(`${appEnv}_${appId}_${cookieName}`, '1', cookieOptions);
    }
  });
}

const PAC_DISABLED_ACTIONS = [
  'fillsign',
  'addsignature',
  'sendforsignature',
  'verb-createwebform',
  'createtemplate',
  'verb-sendinbulk',
  'learn-sign-brandexperience',
  'learn-sign-setuppayments',
  'share-link',
  'upsell',
  'upgrade',
  'start-free-trial',
  'connectors',
  'marketing-integration',
  'complete-account',
];
let listener;

// debug/test info for QE
const limitsInfo = (limits): LimitsInfo => {
  const serverIdleTime = Date.now() - limits.serverActivityTime;
  const timeLeft = Math.max(limits.serverTrustTime - serverIdleTime, 0);
  const floor = n => Math.floor(n * 100.0) / 100;
  return {
    limits: limits.limits,
    timeToExpiry: `${(floor((limits.getLocalExpiry() - Date.now()) / (1000.0 * 60 * 60)))} hours`,
    timeToTrustServer: `${floor(timeLeft / (1000.0 * 60))} minutes`,
  };
};

class Limits2Provider implements IVerbLimitsProvider,
                                 IProviderStatic<typeof Limits2Provider> {
  static getInstance = getSingletonFunction(Limits2Provider);

  serverTrustTime = 5 * 60 * 1000;

  userLimitsDebounce = 5000;

  pacDisabledActions = PAC_DISABLED_ACTIONS;

  serverActivityTime: number;

  syncServerLimits = async useCache => {
    if (!useCache) {
      this.userProvider.clear();
    }
    const newLimits = await this.userProvider.getLimitsVerbs();

    return this.haveLimitsUpdated(newLimits);
  }

  haveLimitsUpdated = newLimits => {
    const filteredLimits = {};
    this.staticLimits = {};
    Object.keys(newLimits).forEach(limit => {
      if (newLimits[limit] && newLimits[limit].limits !== undefined) {
        filteredLimits[limit] = newLimits[limit].limits;
      }
      if (newLimits[limit] && newLimits[limit].configuration !== undefined) {
        this.staticLimits[limit] = newLimits[limit].configuration;
      }
    });
    // Capture expires_at from MeterIQ response for cookie expiry on anonymous flows
    if (typeof newLimits.expires_at === 'number') {
      this.limitsExpiresAt = newLimits.expires_at;
    }
    const serverIdleTime = Date.now() - this.serverActivityTime;
    // trust the server after 5 minutes
    const trustServerLimits = serverIdleTime > this.serverTrustTime;
    // returns a boolean to indicate whether the limits changed
    return this.mergeLimits(filteredLimits, trustServerLimits);
  }

  expiryTimer = null;

  limits: Record<string, Limit> = {};

  staticLimits = {};

  /** Unix timestamp (seconds) from MeterIQ expires_at — used for cookie expiry on anon flows */
  limitsExpiresAt: number | null = null;

  _emitter = new EventEmitter();

  userProvider = null;

  get emitter() {
    return this._emitter;
  }

  handleError = async (operation, _options, errorResult) => {
    const launchOptions = window.adobe_dc_sdk?.launchOptions || {};
    const { operations = [] } = launchOptions.meteriq?.config || {};
    if (errorResult?.details?.status === 429 && operations.includes(operation)) {
      const isUberLimitExceeded = errorResult?.details?.content?.error?.code === UBER_LIMIT_EXCEEDED_ERROR_CODE;

      if (isUberLimitExceeded) {
        const meterIQProvider = await MeterIQLimitsProvider.getInstance();
        try {
          const limits = await meterIQProvider.getLimits(false);
          const changed = this.haveLimitsUpdated(limits);
          this.serverActivityTime = Date.now();
          if (changed) {
            this.publish();
          }
        } catch (e) {
          logging.getLogger('Limits2Provider')
            .error('[UberLimitExceeded] Failed to fetch limits from MeterIQ:', e);
        }
      }
    }
  };

  /**
   * monitor dcapi activity, and optimistically decrement our local limits accordingly
   * This allows our limits to be immediately accurate instead of wating for server
   * eventual consistency.
   */
  listener = (operation, params) => {
    const { headers = {} } = params;
    let verb = headers['x-user-action-name'];

    // For anonymous MeterIQ flows the header is absent — resolve verb from operation/params
    if (!verb && window.adobe_dc_sdk?.launchOptions?.meteriq?.enable) {
      verb = ANON_OPERATION_VERB_MAP[operation];
      if (!verb && operation === 'assets.pdf_actions') {
        const actionKey = params?.content?.pdf_actions?.[0]
          && Object.keys(params.content.pdf_actions[0])[0];
        verb = ANON_PDF_ACTION_VERB_MAP[actionKey];
      }
    }

    let changed = false;

    const decrementRemaining = (limits, key) => {
      if (limits[key] && limits[key].remaining > 0) {
        limits[key].remaining -= 1;
        if (limits.uber && limits.uber.remaining > 0) {
          limits.uber.remaining -= 1;
        }
        return true;
      }
      return false;
    };

    const updateDownloadRemaining = (limits, key) => {
      const limit = limits[key];
      if (limit && typeof limit.download_remaining === 'number' && limit.download_remaining > 0) {
        limit.download_remaining -= 1;
        return true;
      }
      return false;
    };

    switch (operation) {
    case 'assets.createpdf':
    case 'assets.pdf_actions':
    case 'assets.exportpdf':
    case 'assets.splitpdf':
    case 'assets.createpdf_from_html':
      // Optimistically decrement local limits
      changed = decrementRemaining(this.limits, verb);

      // If MeterIQ is enabled, also update the MeterIQ cache optimistically
      this.updateMeterIQCacheFromLocalDecrements(this.limits, verb);
      break;
    case 'assets.download_uri':
      if (verb) {
        changed = updateDownloadRemaining(this.limits, verb);
        this.updateMeterIQCacheFromLocalDecrements({ [verb]: this.limits[verb] }, verb);
      }
      break;
    case 'assets.authorize_client_verb':
      (() => {
        const updateLimits = {
          ...(this.limits[verb] ? { [verb]: { remaining: this.limits[verb].remaining } } : {}),
          ...(this.limits.uber ? { uber: { remaining: this.limits.uber.remaining } } : {}),
        };
        decrementRemaining(updateLimits, verb);
        this.updateMeterIQCacheFromLocalDecrements(updateLimits, verb);
      })();

      break;
    }
    /**
     * Keep track of the last server operation.
     * After a period of time for eventual consistency to settle down, we will trust it again.
     * Dont count operations on users.
     */
    if (!operation.startsWith('user')) {
      this.serverActivityTime = Date.now();
    }
    if (changed) {
      this.publish();
    }
  }

  async updateMeterIQCacheFromLocalDecrements(limits: Record<string, Limit>, actionVerb?: string) {
    if (window.adobe_dc_sdk?.launchOptions?.meteriq?.enable) {
      try {
        const meterIQProvider = await MeterIQLimitsProvider.getInstance();
        meterIQProvider.updateCacheFromLocalDecrements(limits, actionVerb);
      } catch (e) {
        // Ignore errors - cache will be refreshed on next server sync
      }
    }
  }

  async ready() {
    this.userProvider = await providers.user();

    // load limits without clearing them first
    // first time in, we can trust that they're fresh
    await this.loadLimits(true);

    this.scheduleExpiry();

    this.listener = this.listener.bind(this);
    this.handleError = this.handleError.bind(this);
    DcapiAPI.getInstance().then(api => {
      const dcapi = api.getDcapi();
      dcapi.addListener(this.listener);
      if (window.adobe_dc_sdk?.launchOptions?.meteriq?.enable) {
        dcapi.addErrorListener(this.handleError);
      }
    });
    this.serverActivityTime = Date.now();
    if (window.adobe_dc_sdk) {
      window.adobe_dc_sdk.limits2Info = () => limitsInfo(this);
    }
    globalContextManager.onProviderChange('user', () => this.reset({ isTriggeredAfterUserReset: true }),
      { name: 'limits2', isProvider: true });
    return this;
  }

  terminate() {
    DcapiAPI.getInstance().then(api => {
      const dcapi = api.getDcapi();
      dcapi.removeListener(listener);
    });
  }

  cancelExpiry = () => this.expiryTimer && clearTimeout(this.expiryTimer);

  setExpiry = (ms = 24 * 60 * 60 * 1000) => {
    this.setLocalInt(LIMITS_EXPIRY, Date.now() + ms);
  }

  getLocalExpiry = () => {
    let localStorageValue = null;
    try {
      localStorageValue = window.localStorage.getItem(LIMITS_EXPIRY);
    } catch (e) {
      // ignore
    }
    let localVal = null;
    if (localStorageValue !== null) {
      localVal = parseInt(localStorageValue, 10);
    }
    return localVal;
  };

  scheduleExpiry = (reset: boolean = false) => {
    if (window.adobe_dc_sdk?.launchOptions?.meteriq?.enable) {
      // Do not schedule expiry for MeterIQ
      // because if invalid cache, the PDf operations API will throw 429 error, and limits will be reset
      return;
    }

    let expiry = this.getLocalExpiry();
    if (expiry === null || reset) {
      // First time user
      this.setExpiry();
      expiry = this.getLocalExpiry();
    }
    const now = Date.now();
    const howLongToWait = expiry - now;
    this.cancelExpiry();
    if (howLongToWait > 0) {
      this.expiryTimer = setTimeout(
        this.onLimitsExpired,
        howLongToWait,
      );
    }
  }

  onLimitsExpired = async () => {
    await this.reset();
    this.scheduleExpiry(true);
  }

  subscribe = subscriber => {
    this.emitter.addListener('change', subscriber);
  }

  unsubscribe = subscriber => {
    this.emitter.removeListener('change', subscriber);
  }

  limitsExpired = () => {
    const localExpiry = this.getLocalExpiry();
    if (localExpiry === null) {
      return false;
    }
    return (Date.now() > localExpiry);
  };

  loadLimitsTimeout;

  /**
   * this async method cleares the user provider to cause
   * a fresh `user/self` to be retrieved, and then
   * awaits the limits key of the `self` response.
   * This method is debounced so that we don't call it multiple times at startup.
   * Once every five seconds...
   */
  loadLimits = async useCache => {
    const later = () => {
      this.loadLimitsTimeout = null;
    };
    const callNow = !this.loadLimitsTimeout;
    if (callNow) {
      this.loadLimitsTimeout = setTimeout(later, this.userLimitsDebounce);
      const serverChanged = await this.syncServerLimits(useCache);
      if (serverChanged) {
        // today, any change in server limits needs to trigger a re-set
        // on our expiry time.  In the future, we need to do this based on
        // actual times returned by the server
        if (this.limitsExpired()) this.scheduleExpiry(true);
        this.publish();
      }
    }
    return this.limits;
  }

  mergeLimits(newLimits, trustNewLimits) {
    // If the server limits are lower, use them.
    // If they're not lower, then they're probably not current
    // (unless 24 hours has expired)
    // use the trustNewLimits parameter to determine if the input limits are current
    let changed = false;
    const isLower = (newLimit, currentLimit) => {
      if (!newLimit) {
        return false;
      }
      if (!currentLimit) {
        return true;
      }
      return newLimit.remaining < currentLimit.remaining;
    };

    const isDifferent = (newLimit, currentLimit) => {
      if (!newLimit) {
        return false;
      }
      if (!currentLimit) {
        return true;
      }
      return newLimit.remaining !== currentLimit.remaining;
    };

    Object.keys(newLimits).forEach(limit => {
      if (isLower(newLimits[limit], this.limits[limit])
        || (trustNewLimits && isDifferent(newLimits[limit], this.limits[limit]))) {
        this.limits[limit] = newLimits[limit];
        changed = true;
      }
    });
    return changed;
  }

  getExhaustedLimits = () => Object.keys(this.limits).filter(limit => this.limits[limit].remaining === 0);

  getUsedLimits = () => {
    // this is not implemented for signed in users yet
    throw new Error('Not implemented yet');
  }

  getStaticLimits = () => ({ ...this.staticLimits });

  getLimit = limitName => this.limits[limitName] || {};

  canPerform = verb => {
    if (this.isPrivilegedAction(verb)) {
      return false;
    }
    const limit = this.getLimit(verb);
    if (limit.remaining === -1) {
      return true;
    }
    const uberLimit = this.getLimit('uber');
    if (uberLimit.remaining === 0 || limit.remaining === 0) {
      return false;
    }
    return true;
  }

  reset = async (options: {
    shouldTriggerGcmEvent?: boolean,
    isTriggeredAfterUserReset?: boolean,
  } = {}) => {
    const { shouldTriggerGcmEvent = false, isTriggeredAfterUserReset = false } = options;
    this.limits = {};
    clearTimeout(this.loadLimitsTimeout);
    this.loadLimitsTimeout = null;
    await this.loadLimits(isTriggeredAfterUserReset);
    if (shouldTriggerGcmEvent) {
      await globalContextManager.emitProviderChange('limits2', { type: 'RESET' });
    }
  }

  setLocalInt = (item, intValue) => {
    try {
      window.localStorage.setItem(item, intValue.toString());
    } catch (e) {
      // ignore
    }
  }

  /**
   * @description
   * Set disabled actions for PAC and default PAC_DISABLED_ACTIONS list is ignored.
   * @method
   * @param {Array<string>} disabledActions Actions disabled for pac
   * @returns {undefined}
   */
  setDisabledActionsForPAC = (disabledActions = PAC_DISABLED_ACTIONS) => {
    this.pacDisabledActions = [...disabledActions];
  };

  /**
   * @description
   * Checks whether the action is privileged action or not.
   * @method
   * @param {string} action Action name
   * @returns {boolean} Flag indicating whether the operation is privileged or not.
   */
  isPrivilegedAction = action => {
    if (auth2.isIncompleteAccount()) {
      return this.pacDisabledActions.includes(action);
    }
    return false;
  }

  getVerbDownloadRemaining = async (verb: string): Promise<number> => {
    if (!window.adobe_dc_sdk?.launchOptions?.meteriq?.enable) return 0;
    const meterIQProvider = await MeterIQLimitsProvider.getInstance();
    return meterIQProvider.getVerbDownloadRemaining(verb);
  }

  publish = () => {
    const nextExhaustedLimits = [...this.getExhaustedLimits()];
    this.emitter.emit('change', {
      nextExhaustedLimits,
      nextLimits: { ...this.limits },
      nextUsedLimits: undefined, // TODO: replace with [...this.getUsedLimits()] once implemented
    });
    // Write exhausted-limit cookies for anonymous MeterIQ users so other surfaces
    // (acom, chrome extension) can suppress conversion entry points without calling the API
    if (!auth2.isSignedIn && window.adobe_dc_sdk?.launchOptions?.meteriq?.enable) {
      setExhaustedVerbCookies(nextExhaustedLimits, this.limitsExpiresAt);
    }
  }
}

export default Limits2Provider;
